import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider, ChatContext } from './contexts/ChatContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import ImpersonationBanner from './components/ImpersonationBanner';
import ChatSidebar from './components/chat/ChatSidebar';
import theme from './theme';

// Pages
import LoginPage from './pages/LoginPage';
import CommandCenterPage from './pages/CommandCenterPage';
import TimeTrackingPage from './pages/TimeTrackingPage';
import TasksPage from './pages/TasksPage';
import ProjectsPage from './pages/ProjectsPage';
import UsersPage from './pages/UsersPage';
import ReportsPage from './pages/ReportsPage';
import AccountPage from './pages/AccountPage';
import BoardPage from './pages/BoardPage';
import MyWorkPage from './pages/MyWorkPage';
import CustomersPage from './pages/CustomersPage';
import InvoicesPage from './pages/InvoicesPage';
import QuoteLongformPage from './pages/QuoteLongformPage';
import ActivateAccountPage from './pages/ActivateAccountPage';
import MyTaskListPage from './pages/MyTaskListPage';
import AllTasksPage from './pages/AllTasksPage';
import PMHomePage from './pages/PMHomePage';
import ProjectWorkspacePage from './pages/ProjectWorkspacePage';
import TaskDeepLinkPage from './pages/TaskDeepLinkPage';
import { roleHome } from './utils/roleHome';

// Role-aware landing redirect for the root path
function RoleHomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={roleHome(user)} replace />;
}

function AppContent() {
  const location = useLocation();
  const { user, isClient } = useAuth();
  const { isChatOpen, chatWidth } = useContext(ChatContext);
  const isLoginPage = location.pathname === '/login';
  const isActivatePage = location.pathname.startsWith('/activate');
  const isPublicPage = isLoginPage || isActivatePage;
  // Chat is internal-only (presence is org-wide)
  const clientUser = isClient && isClient();
  // Im Ansichtsmodus bleibt der Chat aus: fremde Direktnachrichten gehen den
  // Admin nichts an, und der angesehene User soll nicht als online erscheinen.
  const showCollab = user && !isPublicPage && !clientUser && !user.impersonated_by;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Only show header if user is logged in and not on public pages */}
        {user && !isPublicPage && <ImpersonationBanner />}
        {user && !isPublicPage && <Header />}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: isPublicPage ? 'transparent' : 'background.default',
            transition: 'margin 0.3s ease',
            marginRight: isChatOpen && user && !isPublicPage ? `${chatWidth}px` : 0
          }}
        >
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/activate/:token" element={<ActivateAccountPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute clientAllowed={false}>
                    <CommandCenterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/timetracking"
                element={
                  <ProtectedRoute clientAllowed={false}>
                    <TimeTrackingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute clientAllowed={false}>
                    <TasksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/board"
                element={
                  <ProtectedRoute clientAllowed={false}>
                    <BoardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-work"
                element={
                  <ProtectedRoute clientAllowed={false}>
                    <MyWorkPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-tasklist"
                element={
                  <ProtectedRoute clientAllowed={false}>
                    <MyTaskListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/all-tasks"
                element={
                  <ProtectedRoute clientAllowed={false}>
                    <AllTasksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pm"
                element={
                  <ProtectedRoute>
                    <PMHomePage />
                  </ProtectedRoute>
                }
              />
              {/* Direktlink aus Benachrichtigungsmails: öffnet das Ticket sofort */}
              <Route
                path="/task/:taskId"
                element={
                  <ProtectedRoute>
                    <TaskDeepLinkPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pm/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <ProjectWorkspacePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects"
                element={
                  <ProtectedRoute adminOnly={true}>
                    <ProjectsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute adminOnly={true}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute adminOnly={true}>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute adminOnly={true}>
                    <CustomersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <ProtectedRoute adminOnly={true}>
                    <InvoicesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices/longform/:id"
                element={
                  <ProtectedRoute adminOnly={true}>
                    <QuoteLongformPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
          <Route path="/" element={<RoleHomeRedirect />} />
          <Route path="*" element={<RoleHomeRedirect />} />
        </Routes>
      </Box>
    </Box>
      {/* Chat Sidebar - nur für interne User (Presence ist org-weit) */}
      {showCollab && <ChatSidebar />}
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ChatProvider>
          <Router>
            <AppContent />
          </Router>
        </ChatProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
